import { Alert } from '@material-ui/lab';
import Box from '@material-ui/core/Box';
import React, { useState, useEffect, FC } from 'react';
import { SelectInput, TextInput, useTranslate, required } from 'react-admin';
import { CreateButton, Drawer, TextInput as TextInputComponent } from '~/components';
import { CreateForm } from '~/components/ra';
import { useForm } from 'react-final-form';
import ReferenceField from '~/components/ReferenceField';
import useEventEntity from '~/hooks/eventEntities';
import { isEmpty } from 'lodash';
import { Reference } from '~/types';
import { getEntities } from '~/api/events';
import { useQuery } from 'react-query';
import { formDataMapping } from '~/utils/affiliatedObjects';

interface Props {
  id: string;
  refetch?: () => void;
  resource: 'accounts' | 'individuals' | 'businesses' | 'partners' | 'employees' | 'transactions';
  references?: Reference[];
}

const INITIAL_OBJECT_STATE = { type: '', searchId: '', id: '' };

const AddAffiliatedObject = (props: Props) => {
  const { id, refetch, resource, references } = props;
  const [referenceType, setReferenceType] = useState('');
  const [object, setObject] = useState(INITIAL_OBJECT_STATE);
  const [subObject, setSubObject] = useState(INITIAL_OBJECT_STATE);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setReferenceType('');
    setObject(INITIAL_OBJECT_STATE);
    setSubObject(INITIAL_OBJECT_STATE);
    setOpen(false);
    refetch && refetch();
  };

  const translate = useTranslate();
  const t = (key: string) => translate(`components.AffiliatedObjects.${key}`);

  const { data: entity, isLoading: isLoadingEntity } = useEventEntity(
    { id: object?.searchId, type: object?.type },
    !!object?.type && !!object?.searchId
  );
  const { data: subEntity, isLoading: isLoadingSubEntity } = useEventEntity(
    { id: subObject?.searchId, type: subObject?.type },
    !!subObject?.type && !!subObject?.searchId
  );

  const existingEarningsParentRefs =
    referenceType === 'EARNINGS_PARENT'
      ? references?.filter((r) => !r.deleted && r.referenceType === 'EARNINGS_PARENT') ?? []
      : [];
  const existingAccountIds = existingEarningsParentRefs.map((r) => r.objectId);

  const { data: existingAccountEntities } = useQuery(
    ['earningsParentEntities', existingAccountIds],
    () => getEntities({ ACCOUNT: existingAccountIds }).then((r) => r.data),
    {
      enabled: referenceType === 'EARNINGS_PARENT' && existingAccountIds.length > 0,
      staleTime: Infinity,
    }
  );

  const hasDuplicateCurrency = !!(
    entity?.currency &&
    existingAccountEntities &&
    existingAccountIds.some((id) => existingAccountEntities[id]?.currency === entity.currency)
  );

  const createChoices = (data: any) => {
    return data.map((item: string) => ({
      id: item,
      name: t(item),
    }));
  };

  const formData = formDataMapping[resource];
  const affiliatedObjectKeys = Object?.keys(formData);
  const affiliatedObjectChoices = createChoices(affiliatedObjectKeys);
  const objectTypeKeys = referenceType ? Object?.keys(formData[referenceType]) : [];
  const objectTypeChoices = createChoices(objectTypeKeys);
  const subObjectChoices =
    referenceType && object?.type ? createChoices(formData[referenceType][object?.type]) : [];
  return (
    <>
      <CreateButton variant='add' onClick={handleOpen} />
      <Drawer heading={t('addAffiliatedObject')} open={open} onClose={handleClose}>
        <CreateForm
          disabled={
            !entity ||
            isLoadingEntity ||
            (!isEmpty(subObjectChoices) && (!subEntity || isLoadingSubEntity)) ||
            hasDuplicateCurrency
          }
          resource={resource}
          id={id}
          subresource='references'
          closeParent={handleClose}
        >
          <>
            <SelectInput
              source='referenceType'
              onChange={(e: any) => setReferenceType(e.target.value)}
              label={t('affiliationType')}
              choices={affiliatedObjectChoices}
              validate={required()}
            />
            <SelectInput
              source='objectType'
              value={object?.type}
              onChange={(e: any) => setObject((prev) => ({ ...prev, type: e.target.value }))}
              label={t('objectType')}
              choices={objectTypeChoices}
              disabled={!referenceType}
              validate={required()}
            />
            {object?.type &&
              (object?.type === 'ACCOUNT' ? (
                <AccountIdInput
                  disabled={!object?.type}
                  onBlur={(e: any) => setObject((prev) => ({ ...prev, searchId: e.target.value }))}
                  label={t('accountUUIDOrNumber')}
                />
              ) : (
                <TextInput
                  source='objectId'
                  value={object?.id}
                  // onChange={(e: any) => setObject((prev) => ({ ...prev, id: e.target.value }))}
                  onBlur={(e: any) => setObject((prev) => ({ ...prev, searchId: e.target.value }))}
                  disabled={!object?.type}
                  label={t('objectUUID')}
                  validate={required()}
                />
              ))}
            <FormValueSetter
              objectType={object?.type}
              objectsearchId={object?.searchId}
              entityId={entity?.id}
              subObjectsearchId={subObject?.searchId}
            />
            {object?.searchId &&
              (entity || isLoadingEntity ? (
                <ReferenceField
                  withDefaultIcon
                  record={{ id: entity?.id ?? object?.searchId, type: object?.type }}
                />
              ) : (
                <Alert severity='warning'>
                  {object?.type === 'ACCOUNT' ? t('accountNotFound') : t('warning')}
                </Alert>
              ))}
            {entity && hasDuplicateCurrency && (
              <Box mb={2} mt={2}>
                <Alert severity='warning'>{t('earningsParentDuplicateCurrency')}</Alert>
              </Box>
            )}
            {object?.type && !isEmpty(subObjectChoices) && (
              <Box mb={2} mt={2}>
                <SelectInput
                  source='subObjectType'
                  value={subObject?.type}
                  onChange={(e: any) => setSubObject((prev) => ({ ...prev, type: e.target.value }))}
                  label={t('subObjectType')}
                  choices={subObjectChoices}
                  disabled={!object?.type}
                  validate={required()}
                />
                <TextInput
                  source='subObjectId'
                  value={subObject?.id}
                  onBlur={(e: any) =>
                    setSubObject((prev) => ({ ...prev, searchId: e.target.value }))
                  }
                  // onChange={(e: any) => setSubObject((prev) => ({ ...prev, id: e.target.value }))}
                  label={t('subObjectUUID')}
                  disabled={!object?.type}
                  validate={required()}
                />
              </Box>
            )}
            {subObject?.searchId &&
              !isEmpty(subObjectChoices) &&
              (subEntity || isLoadingSubEntity ? (
                <ReferenceField
                  withDefaultIcon
                  record={{ id: subEntity?.id ?? subObject?.searchId, type: subObject?.type }}
                />
              ) : (
                <Alert severity='warning'>{t('warning')}</Alert>
              ))}
          </>
        </CreateForm>
      </Drawer>
    </>
  );
};

const FormValueSetter: FC<{
  objectType: string;
  objectsearchId: string;
  entityId: string | undefined;
  subObjectsearchId: string;
}> = ({ objectType, objectsearchId, entityId, subObjectsearchId }) => {
  const form = useForm();

  useEffect(() => {
    const objectId = objectType === 'ACCOUNT' ? entityId ?? '' : objectsearchId;
    form.change('objectId', objectId);
  }, [objectType, objectsearchId, entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    form.change('subObjectId', subObjectsearchId);
  }, [subObjectsearchId]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

const AccountIdInput: FC<any> = ({ label, disabled, onBlur }) => {
  const [inputValue, setInputValue] = useState('');
  return (
    <TextInputComponent
      name='accountSearch'
      value={inputValue}
      onChange={(e: any) => {
        setInputValue(e.target.value);
      }}
      onBlur={onBlur}
      label={label}
      disabled={disabled}
      required={true}
    />
  );
};

export default AddAffiliatedObject;
